import { Box, Typography } from '@mui/material';
import type { TimerDisplayShape } from '@/utils/timerDisplay';
import { useChildTokens } from './useChildTokens';

/**
 * Liten, stillastående förhandsvisning av en form (ring, fylld cirkel, tårtbit,
 * urtavla med visare, liggande/stående stapel, inga figurer).
 *
 * Används där klockan väljs, så att valet går att förstå utan att läsa — bilden visar
 * vad man får. Den är avsiktligt statisk (ingen animation) och hämtar alla färger ur
 * temats tokens. Siffrorna ritas här som "12" i stället för mm:ss: rutan är 40 px och
 * ska visa ATT siffror finns, inte vilken tid det är.
 */

const SIZE = 40;
const FACE_BORDER = 2;
const TICK_INSET = 1;
/** Fast "60 % kvar" i förhandsvisningen — bara för att formen ska gå att känna igen. */
const PREVIEW_FRACTION = 0.6;
const PREVIEW_DEGREES = PREVIEW_FRACTION * 360;

type TimerDisplayPreviewProps = {
  shape: TimerDisplayShape;
  /** Accentfärg (CSS-färgvärde ur temat). */
  color: string;
  /** Rita siffror i förhandsvisningen. */
  digits?: boolean;
};

const TimerDisplayPreview = ({ shape, color, digits = false }: TimerDisplayPreviewProps) => {
  const tokens = useChildTokens();

  const digitLabel = (fontSize: string, plate = false) =>
    digits ? (
      <Typography
        sx={{
          position: 'absolute',
          fontSize,
          fontWeight: 900,
          lineHeight: 1,
          color: tokens.text,
          fontVariantNumeric: 'tabular-nums',
          ...(plate && { bgcolor: tokens.card, borderRadius: '4px', px: '2px' }),
        }}
      >
        12
      </Typography>
    ) : null;

  if (shape === 'bar_h') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-bar_h"
        sx={{ width: SIZE, display: 'grid', placeItems: 'center', gap: '3px' }}
      >
        {digits && (
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: tokens.text }}>
            12
          </Typography>
        )}
        <Box
          sx={{
            width: SIZE,
            height: 12,
            borderRadius: '6px',
            bgcolor: tokens.border,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: '60%', height: '100%', borderRadius: '6px', bgcolor: color }} />
        </Box>
      </Box>
    );
  }

  if (shape === 'bar_v') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-bar_v"
        sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: SIZE }}
      >
        <Box
          sx={{
            width: 14,
            height: SIZE,
            borderRadius: '7px',
            bgcolor: tokens.border,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <Box sx={{ width: '100%', height: '60%', borderRadius: '7px', bgcolor: color }} />
        </Box>
        {digits && (
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: tokens.text }}>
            12
          </Typography>
        )}
      </Box>
    );
  }

  if (shape === 'disc') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-disc"
        sx={{
          width: SIZE,
          height: SIZE,
          borderRadius: '50%',
          bgcolor: tokens.border,
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            width: SIZE * Math.sqrt(PREVIEW_FRACTION),
            height: SIZE * Math.sqrt(PREVIEW_FRACTION),
            borderRadius: '50%',
            bgcolor: color,
          }}
        />
        {digitLabel('0.6rem', true)}
      </Box>
    );
  }

  if (shape === 'wedge') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-wedge"
        sx={{
          width: SIZE,
          height: SIZE,
          borderRadius: '50%',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          bgcolor: tokens.card,
          border: `${FACE_BORDER}px solid`,
          borderColor: tokens.border,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            // Samma riktning som TimerWedge: biten ligger moturs från 12.
            background: `conic-gradient(transparent 0deg ${360 - PREVIEW_DEGREES}deg, ${color} ${
              360 - PREVIEW_DEGREES
            }deg)`,
          }}
        />
        {digitLabel('0.6rem', true)}
      </Box>
    );
  }

  if (shape === 'dial') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-dial"
        sx={{
          width: SIZE,
          height: SIZE,
          borderRadius: '50%',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          bgcolor: tokens.card,
          border: `${FACE_BORDER}px solid`,
          borderColor: tokens.border,
          overflow: 'hidden',
        }}
      >
        {[0, 3, 6, 9].map((tick) => (
          <Box
            key={tick}
            sx={{
              position: 'absolute',
              top: TICK_INSET,
              left: '50%',
              width: 2,
              height: 5,
              ml: '-1px',
              borderRadius: '1px',
              bgcolor: tokens.textSoft,
              transformOrigin: `50% ${SIZE / 2 - FACE_BORDER - TICK_INSET}px`,
              transform: `rotate(${tick * 30}deg)`,
            }}
          />
        ))}
        {/* Visaren, i samma vinkel som den riktiga tavlan skulle visa. */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '50%',
            left: '50%',
            width: 2.5,
            height: SIZE / 2 - 8,
            ml: '-1.25px',
            borderRadius: '2px',
            bgcolor: color,
            transformOrigin: '50% 100%',
            transform: `rotate(${PREVIEW_DEGREES}deg)`,
          }}
        />
        <Box
          sx={{ position: 'absolute', width: 5, height: 5, borderRadius: '50%', bgcolor: color }}
        />
        {digits && (
          <Typography
            sx={{
              position: 'absolute',
              bottom: 2,
              fontSize: '0.5rem',
              fontWeight: 900,
              color: tokens.text,
              bgcolor: tokens.card,
              borderRadius: '3px',
              px: '2px',
            }}
          >
            12
          </Typography>
        )}
      </Box>
    );
  }

  if (shape === 'none') {
    return (
      <Box
        aria-hidden
        data-testid="timer-display-preview-none"
        sx={{ width: SIZE, height: SIZE, display: 'grid', placeItems: 'center' }}
      >
        <Typography
          sx={{
            fontSize: '0.9rem',
            fontWeight: 900,
            lineHeight: 1,
            color,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          12:00
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      aria-hidden
      data-testid="timer-display-preview-ring"
      sx={{
        width: SIZE,
        height: SIZE,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        background: `conic-gradient(${color} ${PREVIEW_DEGREES}deg, ${tokens.border} ${PREVIEW_DEGREES}deg)`,
      }}
    >
      <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: tokens.card }} />
      {digitLabel('0.6rem')}
    </Box>
  );
};

export default TimerDisplayPreview;
